<template>
	<div class="project-reports-chart-empty-state">
		<div class="project-reports-chart-empty-state__illustration-container">
			<img
				class="project-reports-chart-empty-state__illustration"
				:src="illustrationSrc"
				alt=""
			/>
		</div>

		<div class="project-reports-chart-empty-state__content-container">
			<div class="project-reports-chart-empty-state__title">
				<SuiIcon :name="icon" />
				{{ $t('project_reports_empty_state_title') }}
			</div>
			<div class="project-reports-chart-empty-state__content">
				{{ content }}
			</div>
			<SuiRelatedContent
				v-if="!hideAddTaskButton"
				type="link"
				as="button"
				icon="icon-add-16"
				@click="onAddTaskClicked"
			>
				{{ $t('project_reports_empty_state_add_task') }}
			</SuiRelatedContent>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { useMfeUtils } from '@/composables';
import {
	ContactType,
	type RetainerPeriod,
	type Contact,
	type Project,
} from '@/generated/graphql';
import { SuiIcon, SuiRelatedContent } from '@developers/scoro-ui';

const props = defineProps<{
	illustrationSrc: string;
	content: string;
	project?: Pick<Project, 'id'> & {
		company?: Pick<Contact, 'id' | 'type'> | null;
	};
	retainerPeriodId?: RetainerPeriod['id'];
	icon: string;
	hideAddTaskButton?: boolean;
}>();

const { rootEmit } = useMfeUtils();

const onAddTaskClicked = () => {
	if (!props.project) {
		return;
	}

	rootEmit('show-event-popup', {
		project_id: props.project.id,
		company_id:
			props.project.company?.type === ContactType.Company
				? props.project.company.id
				: undefined,
		person_id:
			props.project.company?.type === ContactType.Person
				? props.project.company.id
				: undefined,
		retainer_period_id: props.retainerPeriodId,
		eventType: 'task',
	});
};
</script>

<style lang="scss" scoped>
.project-reports-chart-empty-state {
	position: relative;
	display: flex;
	align-items: center;
	justify-content: center;

	&__illustration {
		display: block;
		min-height: 35.25rem;
	}

	&__content-container {
		position: absolute;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 2rem;
		max-width: 40rem;
	}

	&__title {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		flex-direction: column;
		color: $neutral-90;
		font-size: 2.25rem;
		line-height: 2.75rem;
		font-weight: $scoro-fw-semi-bold;
		text-align: center;
	}

	&__content {
		color: $neutral-80;
		font-size: 1.625rem;
		line-height: 2.5rem;
		text-align: center;
	}
}
</style>
